import { useEffect, useRef } from 'react'

import { useValueSetter } from 'slab/hooks/useValueSetter'

function normalizeValue (value?: unknown) {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

function autoFormatValue (value: unknown, { decimalPlaces = 2, forceDecimals = false } = {}) {
  const strippedValue = normalizeValue(value).replace(/[^\d.]/g, '')

  if (strippedValue === '') return ''

  const [integer, decimal] = strippedValue.split('.')

  const parsedInteger = parseInt(integer || '0', 10)
  const formattedInteger = new Intl.NumberFormat('en-US', {
    style: 'decimal',
  }).format(parsedInteger)

  if (decimalPlaces <= 0) return formattedInteger

  if (forceDecimals) {
    if (decimal) {
      return `${formattedInteger}.${decimal.slice(0, Math.min(decimalPlaces, decimal.length)).padEnd(decimalPlaces, '0')}`
    } else {
      return `${formattedInteger}.${''.padEnd(decimalPlaces, '0')}`
    }
  } else {
    switch (decimal) {
      case undefined:
        return formattedInteger
      case '':
        return `${formattedInteger}.`
      default: {
        const slicedDecimal = decimal.slice(
          0,
          Math.min(decimalPlaces, decimal.length)
        )

        return `${formattedInteger}.${slicedDecimal}`
      }
    }
  }
}

function changeValue (input: HTMLInputElement, value: string) {
  const setValue = Object?.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set

  setValue?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

function getNextCursorPosition (
  prevValue: string,
  newValue: string,
  cursorPosition: number
) {
  // If the cursor was at the end of the input, just keep it there.
  if (prevValue.length === cursorPosition) {
    return newValue.length
  }

  const oldRawValue = prevValue.slice(0, cursorPosition).replace(/[^\d.]/g, '')
  const newRawValue = newValue.slice(0, cursorPosition).replace(/[^\d.]/g, '')

  const offset = oldRawValue.length - newRawValue.length

  return cursorPosition + offset
}

interface UseNumberMaskArgs {
  decimalPlaces?: number
}

export function useNumberMask ({
  decimalPlaces = 2,
}: UseNumberMaskArgs = {}) {
  const inputState = useRef({
    deleting: false,
    editing: false,
    enteringOwnSeparators: false,
  })
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const input = ref.current

    if (input) {
      input.value = autoFormatValue(input.value, { decimalPlaces, forceDecimals: true })
    }
  }, [decimalPlaces])

  // Uses a custom setter for the input value to autoformat the value if
  // it is mutated directly via the DOM. Libraries like `react-hook-form` do
  // this, for example.
  useValueSetter(ref, (value) => {
    if (!inputState.current.editing) {
      return autoFormatValue(value, { decimalPlaces })
    }
  }, [decimalPlaces])

  useEffect(() => {
    const input = ref.current

    if (input) {
      function handleBlur (event: FocusEvent) {
        inputState.current.enteringOwnSeparators = false
        inputState.current.editing = false

        const currentTarget = event.currentTarget as HTMLInputElement
        const formattedValue = autoFormatValue(currentTarget.value, {
          decimalPlaces,
          forceDecimals: true,
        })

        changeValue(currentTarget, formattedValue)
      }

      function handleFocus () {
        inputState.current.editing = true
      }

      function handleKeyDown (event: KeyboardEvent) {
        if (event.key === ',') {
          // Toggle the `enteringOwnSeparators` state when the user enters the
          // thousdands separator. Note that this currently assumes the user is
          // using English conventions for number formatting.
          if (inputState.current.enteringOwnSeparators === true) {
            inputState.current.enteringOwnSeparators = false
          } else {
            inputState.current.enteringOwnSeparators = true
          }
        }

        if (event.key === '.') {
          inputState.current.enteringOwnSeparators = false
        }

        if (event.key === 'Backspace' || event.key === 'Delete') {
          inputState.current.deleting = true
        } else {
          inputState.current.deleting = false
        }
      }

      function handleInput (event: Event) {
        const currentTarget = event.currentTarget as HTMLInputElement

        const value = currentTarget.value
        const formattedValue = autoFormatValue(value, { decimalPlaces })
        const cursorPosition = currentTarget.selectionEnd || 0

        let nextValue
        let nextCursorPosition

        // If the user is entering their own separators or deleting characters,
        // don't autoformat the value. It's a confusing experience for them.
        // We'll resume autoformatting once they've entered a properly formatted
        // value, or if they enter another separator before its valid. We
        // autoformat the value on blur, so we don't need to worry about
        // the user leaving the separator in an invalid position.
        if (
          inputState.current.enteringOwnSeparators ||
          inputState.current.deleting
        ) {
          const strippedValue = value.replace(/[^\d.,]/g, '')

          if (strippedValue === formattedValue) {
            inputState.current.enteringOwnSeparators = false
          }

          nextValue = strippedValue
          nextCursorPosition = cursorPosition
        } else {
          nextValue = formattedValue
          nextCursorPosition = getNextCursorPosition(
            value,
            nextValue,
            cursorPosition
          )
        }

        currentTarget.value = nextValue
        currentTarget.setSelectionRange(nextCursorPosition, nextCursorPosition)
      }

      input.addEventListener('blur', handleBlur)
      input.addEventListener('focus', handleFocus)
      input.addEventListener('keydown', handleKeyDown)
      input.addEventListener('input', handleInput)

      return () => {
        input.removeEventListener('blur', handleBlur)
        input.removeEventListener('focus', handleFocus)
        input.removeEventListener('keydown', handleKeyDown)
        input.removeEventListener('input', handleInput)
      }
    }
  }, [decimalPlaces])

  return { ref }
}
